import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Shield, LogOut, Users, Package, FileText, DollarSign,
  TrendingUp, TrendingDown, Activity, CalendarIcon, Search,
  Edit2, Loader2, UserCheck, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, formatDateTime, cn } from '../lib/utils';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const SUBSCRIPTION_COLORS = {
  free: '#94A3B8',
  basic: '#3B82F6',
  pro: '#F59E0B',
};

function KPICard({ title, value, subtitle, icon: Icon, trend, trendValue, color = "bg-slate-700" }) {
  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
        {trend && (
          <div className="mt-4 flex items-center gap-1">
            {trend === 'up' ? (
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-400" />
            )}
            <span className={trend === 'up' ? 'text-emerald-400' : 'text-red-400'}>
              {trendValue}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UserRow({ user, onEdit }) {
  const getSubscriptionBadge = (type) => {
    const colors = {
      free: 'bg-slate-600 text-slate-200',
      basic: 'bg-blue-600 text-blue-100',
      pro: 'bg-amber-600 text-amber-100',
    };
    return colors[type] || colors.free;
  };

  return (
    <tr className="border-b border-slate-700 hover:bg-slate-750">
      <td className="py-4 px-4">
        <div>
          <p className="font-medium text-white">{user.email}</p>
          <p className="text-sm text-slate-400">{user.company_name || 'No company'}</p>
        </div>
      </td>
      <td className="py-4 px-4">
        <Badge className={getSubscriptionBadge(user.subscription_type)}>
          {user.subscription_type?.toUpperCase() || 'FREE'}
        </Badge>
      </td>
      <td className="py-4 px-4">
        <span className="text-slate-300">{user.subscription_end_date ? formatDate(user.subscription_end_date) : '-'}</span>
      </td>
      <td className="py-4 px-4">
        <span className="text-slate-300 font-mono">{user.shipment_count}</span>
      </td>
      <td className="py-4 px-4">
        <span className="text-slate-400 text-sm">{user.last_access ? formatDateTime(user.last_access) : 'Never'}</span>
      </td>
      <td className="py-4 px-4">
        <span className="text-slate-400 text-sm">{formatDate(user.created_at)}</span>
      </td>
      <td className="py-4 px-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => onEdit(user)}
          className="text-slate-400 hover:text-white"
          data-testid={`edit-user-${user.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
}

function EditSubscriptionDialog({ open, onOpenChange, user, onSave }) {
  const [loading, setLoading] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState('free');
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (user) {
      setSubscriptionType(user.subscription_type || 'free');
      setEndDate(user.subscription_end_date ? new Date(user.subscription_end_date) : null);
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await adminAPI.updateSubscription(user.id, {
        subscription_type: subscriptionType,
        subscription_end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
      });
      toast.success('Subscription updated successfully');
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white">Edit Subscription</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update subscription for {user.email}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Subscription Type</Label>
            <Select value={subscriptionType} onValueChange={setSubscriptionType}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="basic">Basic ($9.99/mo)</SelectItem>
                <SelectItem value="pro">Pro ($59/mo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Subscription End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-slate-700 border-slate-600 text-white",
                    !endDate && "text-slate-500"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "PPP") : "No end date (unlimited)"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-700 border-slate-600">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {endDate && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setEndDate(null)}
                className="text-slate-400 hover:text-white"
              >
                Clear end date
              </Button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-600 text-slate-300">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            data-testid="save-subscription-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [usersRes, kpisRes] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getKPIs(),
      ]);
      setUsers(usersRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.company_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Prepare chart data
  const subscriptionChartData = kpis ? Object.entries(kpis.users.by_subscription || {}).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
    fill: SUBSCRIPTION_COLORS[key] || '#94A3B8',
  })) : [];

  const statusChartData = kpis ? Object.entries(kpis.shipments.by_status || {}).map(([key, value]) => ({
    name: key.replace('_', ' ').charAt(0).toUpperCase() + key.replace('_', ' ').slice(1),
    value,
  })) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-white">ImportDocs Admin</h1>
              <p className="text-sm text-slate-400">System Dashboard</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-slate-400 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="p-6">
        <Tabs defaultValue="kpis" className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="kpis" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
              KPIs Dashboard
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
              User Management
            </TabsTrigger>
          </TabsList>

          {/* KPIs Tab */}
          <TabsContent value="kpis">
            {/* KPI Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPICard 
                title="Total Users" 
                value={kpis?.users?.total || 0}
                subtitle={`${kpis?.users?.new_this_month || 0} new this month`}
                icon={Users}
                color="bg-blue-600"
              />
              <KPICard 
                title="Active Users (30d)" 
                value={kpis?.users?.active_last_30_days || 0}
                subtitle={`${((kpis?.users?.active_last_30_days / kpis?.users?.total) * 100 || 0).toFixed(0)}% of total`}
                icon={UserCheck}
                color="bg-emerald-600"
              />
              <KPICard 
                title="Total Shipments" 
                value={kpis?.shipments?.total || 0}
                icon={Package}
                color="bg-purple-600"
              />
              <KPICard 
                title="Documents Uploaded" 
                value={kpis?.documents?.total_uploaded || 0}
                icon={FileText}
                color="bg-cyan-600"
              />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Cost Overview */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Cost Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Total Estimated</span>
                      <span className="text-white font-mono font-bold">{formatCurrency(kpis?.costs?.total_estimated || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Total Actual</span>
                      <span className="text-white font-mono font-bold">{formatCurrency(kpis?.costs?.total_actual || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-300">Variance</span>
                      <span className={`font-mono font-bold ${(kpis?.costs?.variance || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {(kpis?.costs?.variance || 0) > 0 ? '+' : ''}{formatCurrency(kpis?.costs?.variance || 0)}
                        <span className="text-sm ml-2">({(kpis?.costs?.variance_percentage || 0).toFixed(1)}%)</span>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subscriptions Pie Chart */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Users by Subscription</CardTitle>
                </CardHeader>
                <CardContent>
                  {subscriptionChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={subscriptionChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {subscriptionChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-slate-500">
                      No subscription data
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Shipments by Status */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Shipments by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {statusChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={statusChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                      <YAxis stroke="#94A3B8" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155' }} />
                      <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-500">
                    No shipment data
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">All Users</CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input 
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="search-users-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Subscription</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Expires</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Shipments</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Last Access</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Joined</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-slate-500">
                            {searchTerm ? 'No users match your search' : 'No users found'}
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((user) => (
                          <UserRow key={user.id} user={user} onEdit={handleEditUser} />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <EditSubscriptionDialog 
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        user={editingUser}
        onSave={loadData}
      />
    </div>
  );
}
